"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Brain,
  Sparkles,
  Crown,
  Stethoscope,
  Heart,
  Compass,
  Eye,
  Building2,
  Target,
  Scale,
  Lock,
  GitBranch,
  ArrowRight,
  Mic,
  Award,
  CheckCircle2,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { TreeBlock } from "@/lib/blocks/tree-block";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";
import { BookOpen, Quote } from "lucide-react";

const BELIEF_TREE: {
  id: string;
  label: string;
  body: string;
  icon?: React.ReactNode;
  accent?: "red" | "purple" | "cyan" | "emerald" | "amber-gold" | "denim" | "lime" | "coral";
  children?: { id: string; label: string; body: string; accent?: "red" | "purple" | "cyan" | "emerald" | "amber-gold" | "denim" | "lime" | "coral" }[];
}[] = [
  {
    id: "excellence",
    label: "Excellence Is a System, Not a Value",
    body: "The foundational belief. If the operating steps cannot be written down, the outcome is luck, not excellence. What medicine systematized because lives depended on it, organizations must systematize because outcomes demand it.",
    icon: <Target className="size-4" />,
    accent: "red",
    children: [
      { id: "e1", label: "Five-Step Methodology", body: "Discovery, Research, Monitor, Analysis, Execution — the organizational expression of clinical discipline.", accent: "red" },
      { id: "e2", label: "Outcome Over Effort", body: "Medicine does not credit a physician for how hard the attempt was. The patient lived or did not. Verified results are the only metric.", accent: "purple" },
      { id: "e3", label: "Systems Over Values", body: "Values are stated. Systems are executed. The six C's operationalize the philosophy into protocols that can be replicated.", accent: "cyan" },
    ],
  },
  {
    id: "talent",
    label: "The Best Operators Are the Ones the World Overlooks",
    body: "The largest, most reliable pool of operational talent on earth is the one half of humanity the world is still not betting on. The 70% Project is the evidence — a decade of operating data.",
    icon: <BookOpen className="size-4" />,
    accent: "emerald",
    children: [
      { id: "t1", label: "Communication", body: "Problems surface earlier. In sovereign-scale operations, this is an operational advantage that compounds across every engagement.", accent: "emerald" },
      { id: "t2", label: "Risk Discipline", body: "Teams managed risk more carefully — following the protocols that distinguish operational excellence from operational luck.", accent: "cyan" },
      { id: "t3", label: "Retention", body: "Sovereign-scale operations depend on institutional knowledge. High retention is not HR — it is an operational multiplier.", accent: "amber-gold" },
    ],
  },
  {
    id: "diagnosis",
    label: "Diagnosis Before Treatment",
    body: "The first and most neglected leadership skill. Differential diagnosis — asking what else this could be — prevents the organizational equivalent of misdiagnosis.",
    icon: <Compass className="size-4" />,
    accent: "cyan",
    children: [
      { id: "d1", label: "Triage Under Pressure", body: "What is fatal now. What can wait. What must be monitored. What can be safely ignored. The skill that determines outcomes.", accent: "cyan" },
      { id: "d2", label: "Protocol with Judgment", body: "Protocols carry through the ninety-five percent. Judgment handles the five percent. The architecture of operational excellence.", accent: "purple" },
    ],
  },
  {
    id: "dark",
    label: "Building in the Dark",
    body: "The things that endure are built without an audience. A decade of operating in discretion has proven this. The organization does not announce its involvement. It simply produces results.",
    icon: <CheckCircle2 className="size-4" />,
    accent: "amber-gold",
    children: [
      { id: "b1", label: "Duration Is Proof", body: "The infrastructure that took a decade to build cannot be replicated without that same decade of investment. This is the moat.", accent: "amber-gold" },
      { id: "b2", label: "Discretion Creates Trust", body: "Publicity creates visibility. Discretion creates trust. At sovereign altitude, trust is more valuable than visibility.", accent: "denim" },
    ],
  },
  {
    id: "governance",
    label: "Good Governance Wins Elections",
    body: "When citizens can answer what is in it for them in terms they understand, and government can perform, governance becomes the winning strategy. Verified through fifteen victories out of eighteen campaigns.",
    icon: <Quote className="size-4" />,
    accent: "purple",
    children: [
      { id: "g1", label: "Governance Delivery as Strategy", body: "Governance delivery is the most powerful campaign strategy. The approach converts political authority into measurable public outcomes.", accent: "purple" },
      { id: "g2", label: "Citizen-First Outcomes", body: "Digital governance, citizen engagement, and policy implementation through the governance platforms. Citizens first, outcomes measured, systems over charisma.", accent: "emerald" },
    ],
  },
];

const EIGHT_POINT_CREED = [
  { num: "01", title: "Excellence Is Engineered, Not Inspired", body: "If the operating steps cannot be written down, the outcome is luck. Systems produce repeatable outcomes. Luck does not.", accent: "red" as const },
  { num: "02", title: "Diagnosis Before Treatment", body: "Differential diagnosis — asking what else this could be — prevents the organizational equivalent of misdiagnosis.", accent: "purple" as const },
  { num: "03", title: "Triage Is Survival", body: "When everything is urgent, the ability to rank — accurately, under pressure, with incomplete information — determines outcomes.", accent: "cyan" as const },
  { num: "04", title: "Protocol with Judgment", body: "The ninety-five percent follows the pattern. The five percent requires judgment. The architecture reserves human judgment for the exceptions that matter.", accent: "emerald" as const },
  { num: "05", title: "Outcome Over Effort", body: "The patient lived or did not. Verified results are the only acceptable metric. Effort narratives are the enemy of accountability.", accent: "amber-gold" as const },
  { num: "06", title: "Discretion Is Trust", body: "The absence of publicity is the presence of trust. Sovereign-level trust is earned through the absence of breaches, leaks, and publicity.", accent: "denim" as const },
  { num: "07", title: "The Talent the World Overlooks Outperforms", body: "The operating advantages of teams built on execution rather than signal — communication, risk discipline, collaboration, retention, ego management — compound across every engagement.", accent: "lime" as const },
  { num: "08", title: "Build to Last", body: "Duration is the proof of excellence. The Create-Achieve-Innovate cycle never stops. The infrastructure that took a decade to build is the foundation for the next decade of growth.", accent: "coral" as const },
];

const LITHVIK_TENETS = [
  { title: "Lucidus", body: "Clarity — every engagement begins with transparent objectives and decisive strategies driven by data-driven insights. We do not speculate. We know.", accent: "red" as const },
  { title: "Infinium", body: "Infinity — committed to continuous advancement in AI, ML, and next-generation technologies through ongoing R&D investment.", accent: "purple" as const },
  { title: "Technologia", body: "Technology — build what does not exist. Deploy what others have not imagined. Operate platforms on infrastructure most organizations cannot access.", accent: "cyan" as const },
  { title: "Honorificus", body: "Honor — uphold ethical dealings and unwavering client confidentiality. What is done, how it is done, and for whom — none of it is ever spoken beyond the teams that need to know.", accent: "emerald" as const },
  { title: "Victoria", body: "Victory — committed to winning outcomes. Not partial victories. Not moral victories. Real, measurable, verifiable wins.", accent: "amber-gold" as const },
  { title: "Imperium", body: "Empire — an imperial approach to technology and strategy deployment — commanding in capability, sovereign in discretion, unyielding in commitment to client outcomes.", accent: "denim" as const },
  { title: "Kingdomus", body: "Kingdom — build kingdoms of influence, security, and reputation — systems that cannot be penetrated, platforms that cannot be replicated, principles that cannot be compromised.", accent: "lime" as const },
];

const TEN_VALUES = [
  { title: "Integrity", body: "Trustworthiness and honesty in every engagement — the foundation of sovereign-level trust." },
  { title: "Innovation", body: "AI, ML, and state-of-the-art technology deployment — building what does not exist, deploying what others have not imagined." },
  { title: "Excellence", body: "Collaboration, inclusivity, and continuous learning — excellence as a system, not a slogan." },
  { title: "Adaptability", body: "Fluency across diverse cultures, languages, and regulatory environments — eighteen countries, fifteen-plus languages, three continents." },
  { title: "Discretion", body: "Strict confidentiality and compartmentalized operations — zero information leakage, operational security as religion." },
  { title: "Responsibility", body: "Ethical business conduct and corporate governance — projects with questionable motives are declined." },
  { title: "Commitments", body: "Every commitment honored with absolute fidelity — the operational standard that sustains sovereign-level trust." },
  { title: "Confidentiality", body: "Client information is sacred — compartmentalized where every team sees only what it needs to see." },
  { title: "Comfortability", body: "Clients operate with complete confidence in discretion and capability — the compound effect of zero security incidents." },
  { title: "Capability", body: "Engagements accepted only where capabilities guarantee success — the discipline of saying no when the capability is not sovereign-grade." },
];

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const PRINCIPLES = [
  { num: "01", title: "Diagnose Before Treating", body: "Differential diagnosis — the discipline of asking what else this could be — is applied to organizations before any intervention. The clinical method demands a complete picture before action. Most organizational failures trace to leaders who treated the symptom while the disease progressed undetected.", accent: "red" },
  { num: "02", title: "Triage Under Absolute Pressure", body: "What is fatal now. What can wait. What must be monitored. What can be safely ignored. When every issue screams urgency, the ability to rank — accurately, under pressure, with incomplete information — is the skill that determines outcomes.", accent: "purple" },
  { num: "03", title: "Run the Protocol; Keep the Judgment", body: "Protocols carry through the ninety-five percent of cases that follow the pattern. Judgment handles the five percent that do not. The platforms run on protocol. The operator keeps the judgment. This is the architecture of operational excellence.", accent: "cyan" },
  { num: "04", title: "Own the Outcome, Not the Effort", body: "Medicine does not credit a physician for how hard the attempt was. The patient lived or did not. Organizations have lost this accountability. Dr. Kush restores it — every engagement is measured by verified outcomes.", accent: "emerald" },
  { num: "05", title: "Composure as Infrastructure", body: "The delivery room teaches that the senior person&apos;s state determines the team&apos;s state. The discipline of maintaining internal composure so that the team can function is not emotional intelligence — it is operational infrastructure at sovereign scale.", accent: "amber-gold" },
  { num: "06", title: "First, Do No Harm", body: "The oldest principle in medicine. Under pressure, most leaders make the situation worse. The discipline of not making it worse is the most underrated operational skill in leadership. It is the first filter through which every decision must pass.", accent: "denim" },
];

const CORE_BELIEFS = [
  { title: "Excellence Is a System, Not a Value", body: "If the operating steps cannot be written down, the outcome is luck, not excellence. What medicine systematized because lives depended on it, organizations must systematize because outcomes demand it." },
  { title: "The Best Operators Are the Ones the World Overlooks", body: "The largest, most reliable pool of operational talent on earth is the one half of humanity the world is still not betting on. The 70% Project is the evidence." },
  { title: "Diagnosis Before Treatment", body: "Most organizations act on the symptom. The disease progresses undetected. The discipline of forming a complete picture before intervening is the first and most neglected leadership skill." },
  { title: "Building in the Dark", body: "Publicity is not impact. The things that endure are built without an audience. A decade of operating in discretion has proven this. The organization does not announce its involvement. It simply produces results." },
  { title: "The Clinical Method Scales", body: "Diagnose. Triage. Run the protocol. Keep the judgment. Own the outcome. Do no harm. These six principles, learned in a delivery room, have been proven across eighteen countries and the integrated platform ecosystem." },
  { title: "Good Governance Wins Elections", body: "When citizens can answer what is in it for them in terms they understand, and government can perform, governance becomes the winning strategy. Verified through fifteen victories out of eighteen campaigns." },
];

const PHILOSOPHIES = [
  {
    icon: Building2,
    title: "The Building Philosophy",
    body: "Consultants advise and move on. Builders stay, scale, and sustain. The proof of operational excellence is duration. The infrastructure that took a decade to build cannot be replicated without that same decade of investment. This is the moat.",
    accent: "amber-gold",
  },
  {
    icon: Lock,
    title: "The Discretion Philosophy",
    body: "The things that endure are built without an audience. The organization does not announce its involvement. It does not claim credit publicly. It does not name clients, countries, or outcomes. The work speaks through results, not publicity.",
    accent: "denim",
  },
  {
    icon: GitBranch,
    title: "The Integration Philosophy",
    body: "Most leaders specialize in a single domain. The career operates across three — medicine, technology, and politics — and the intersection is where the value is unmatched. One integrated operating method, not three separate capabilities.",
    accent: "cyan",
  },
  {
    icon: Eye,
    title: "The Intelligence Philosophy",
    body: "Intuition is pattern recognition based on limited data. Intelligence is pattern recognition based on complete data. The difference matters because organizations that act on intuition make decisions based on how things seem. Organizations that act on intelligence make decisions based on how things are.",
    accent: "purple",
  },
  {
    icon: Target,
    title: "The Governance Philosophy",
    body: "When citizens can answer what is in it for them in terms they understand, and government can perform, governance becomes the winning strategy. The approach converts political authority into measurable public outcomes through digital governance.",
    accent: "red",
  },
  {
    icon: Scale,
    title: "The Sovereignty Philosophy",
    body: "Strategic sovereignty is not a service line. It is a discipline — the ability of an entity to control its own narrative, protect its own information, govern its own territory, and execute its own strategy without dependence on external actors.",
    accent: "emerald",
  },
];

const SIX_CS = [
  { letter: "C", title: "Command", body: "We do not advise. We command outcomes. That command carries accountability — for the results delivered and for the methods used to deliver them." },
  { letter: "C", title: "Control", body: "Every variable is managed. Every risk is mitigated. Ethical risk is among the variables controlled — systematically, proactively, and without exception." },
  { letter: "C", title: "Converge", body: "Intelligence, strategy, and technology unified into one operational architecture. Convergence eliminates the fragmentation that allows gaps to hide in the spaces between siloed teams." },
  { letter: "C", title: "Conceal", body: "Absolute discretion. Zero information leakage. Operational security as religion. The commitment to confidentiality protects clients — and ensures that sensitive information is guarded with the highest standards of custodial responsibility." },
  { letter: "C", title: "Calculate", body: "Data-driven, analytics-powered, precision-targeted execution. Calculation eliminates the guesswork that leads to unintended consequences. No action on assumption. Action on intelligence." },
  { letter: "C", title: "Conquer", body: "The objective is not participation. The objective is victory. But victory achieved through unethical means is not victory — it is liability. Victory within boundaries, because sustainable outcomes require sustainable methods." },
];

const VISION_PILLARS = [
  { icon: Target, title: "Excellence as a System", body: "Excellence as an engineering problem, not an aspirational value. If the operating steps cannot be written down, the outcome is luck. Systems produce repeatable outcomes; luck does not.", accent: "red" },
  { icon: Heart, title: "Women in Leadership Through Evidence", body: "The 70% Project — proving through a decade of operating data that the talent the world overlooks is the talent that outperforms. The case made from operations, not morality.", accent: "emerald" },
  { icon: Compass, title: "Bridge Three Domains", body: "Clinical rigor applied to technological systems under political-grade stakes. The triple threat as one integrated operating method. The intersection no other executive occupies.", accent: "cyan" },
  { icon: Scale, title: "Governance Transformation", body: "Convert political authority into measurable public outcomes through digital governance, citizen engagement, and policy implementation. Good governance always is the best governance.", accent: "purple" },
  { icon: GitBranch, title: "Organizations That Last", body: "Construct consequential institutions through systems, not charisma — measured by outcomes, not publicity. The Create-Achieve-Innovate cycle never stops. From small to large. From imaginable to unimaginable.", accent: "amber-gold" },
];

export default function PhilosophyPage() {
  return (
    <>
      <PageHero
        icon={Brain}
        overline="Philosophy & Vision"
        overlineClassName="text-emerald"
        title={
          <>
            Excellence Is a System. Vision Is a Discipline. <span className="text-gradient-premium">Philosophy Is the Architecture.</span>
          </>
        }
        subtitle="A set of beliefs forged in clinical practice, tested across eighteen countries, and proven through a decade of sovereign-scale operations. Every engagement, every platform, every team is built on a philosophy that can be written down, taught, and replicated."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "About", href: "/about/" }, { label: "Philosophy" }]}
        badge="The architecture behind the operator"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
            <Sparkles className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Executive Summary</span>
          </div>
          <h2 className="text-display-md mb-5">The operating system behind the operator.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The philosophy is not a collection of motivational principles. It is an operating system — a disciplined set of beliefs that governs how organizations are diagnosed, teams are built, platforms are run, and verified outcomes are produced. The philosophy draws from three sources: clinical medicine, sovereign-scale technology, and political strategy.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            Every belief below is grounded in verified operational data — fifteen victories out of eighteen campaigns, over three hundred elite clients served, zero security incidents across fifteen-plus years, and teams consistently built to seventy percent women by selection, not slogan.
          </p>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Compass className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Belief Tree</span>
          </div>
          <h2 className="text-display-md mb-4">Five root beliefs, each with operational children.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The philosophy is not flat. It is hierarchical — root beliefs that branch into operational principles. Each root is grounded in verified outcomes. Each child is the protocol that executes the belief.
          </p>
        </div>
        <TreeBlock entries={BELIEF_TREE} />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
              <Stethoscope className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">The Medical Foundation</span>
            </div>
            <h2 className="text-display-md mb-4">What medicine teaches about leadership.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Medicine teaches what business schools do not. Evidence over intuition. Protocol under pressure. Triage when everything is urgent. Accountability not for effort but for outcome. The gynaecology specialization added the intimate understanding of women&apos;s health that directly informs The 70% Project.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The medical environment also instilled a tolerance for ambiguity that no MBA program replicates. A physician does not wait for perfect information before acting. Perfect information arrives too late — the patient cannot wait. This tolerance for operating under uncertainty, combined with evidence-based decision-making, is the precise skill set demanded by sovereign-scale operations.
            </p>
          </div>
          <div className="space-y-2">
            {CORE_BELIEFS.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, x: 16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="glass-card rounded-xl p-5 bg-gradient-to-br from-cyan/10 to-transparent border border-cyan/20 hover:scale-[1.005] transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0">
                    <CheckCircle2 className="size-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-heading-sm mb-1.5 text-cyan">{b.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{b.body}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Stethoscope className="size-3.5 text-red" />
            <span className="text-overline text-red">The Operating Method</span>
          </div>
          <h2 className="text-display-md mb-4">Six principles drilled into muscle memory.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The operating method is six principles drilled into muscle memory through years of medical practice, where the cost of failure is measured in human lives. These are not metaphors — they are the actual operating system.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PRINCIPLES.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`text-display-sm font-bold ${a.text} opacity-30 leading-none mb-3`}>{p.num}</div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{p.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"]?.pill ?? "glass"} mb-4`}>
            <BookOpen className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Eight-Point Creed</span>
          </div>
          <h2 className="text-display-md mb-4">The operational creed that holds under pressure.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Eight principles that govern every decision, every engagement, and every team. These are the beliefs that hold regardless of context, pressure, or stake. The principles have been tested across eighteen countries and a decade of sovereign-scale operations.
          </p>
        </div>
        <PillarGridBlock pillars={EIGHT_POINT_CREED} columns={2} variant="numbered" />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-4`}>
            <Heart className="size-3.5 text-lime" />
            <span className="text-overline text-lime">The 70% Philosophy</span>
          </div>
          <h2 className="text-display-md mb-4">The operating case for women in leadership.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The teams are, in most offices, about seventy percent women. People assume this is a values statement. It is not. It is an operating decision with a decade of evidence behind it. The number is a consequence, not a target.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { num: "01", title: "Communication", body: "Problems surface earlier because team members are willing to say the uncomfortable thing in the room before it becomes a catastrophe in the boardroom." },
            { num: "02", title: "Risk Discipline", body: "Higher adherence to protocol without sacrificing the judgment needed for exceptional situations." },
            { num: "03", title: "Collaboration", body: "Internal energy goes outward, toward the mission, instead of inward, toward each other." },
            { num: "04", title: "Retention", body: "Institutional knowledge preserved because the environment is one where people can stay and thrive." },
            { num: "05", title: "Ego Management", body: "Ego interferes less with execution, allowing operational systems to function as designed." },
          ].map((t, i) => (
            <motion.div
              key={t.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="relative glass-card rounded-2xl p-5 bg-gradient-to-br from-lime/10 to-transparent border border-lime/20"
            >
              <div className="text-display-sm font-bold text-lime/30 leading-none mb-3">{t.num}</div>
              <h3 className="text-heading-md mb-2 text-lime">{t.title}</h3>
              <p className="text-caption text-muted-foreground leading-relaxed">{t.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Compass className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Domain Philosophies</span>
          </div>
          <h2 className="text-display-md mb-4">Six philosophies that compound.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each domain has a philosophy that compounds. Together, they form the architecture behind the operator. None are values to be discussed — they are protocols to be executed.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PHILOSOPHIES.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-3.5">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg ring-2 ${a.ring} shrink-0`}>
                    <p.icon className="size-6 text-white" />
                  </div>
                  <div>
                    <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Award className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Six C&apos;s</span>
          </div>
          <h2 className="text-display-md mb-4">The execution architecture.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The execution philosophy operationalized into six protocols. Values are stated. Systems are executed. These are not values to be discussed — they are protocols to be executed.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SIX_CS.map((c, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-3.5">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg ring-2 ${a.ring} shrink-0`}>
                    <span className="text-heading-md font-bold text-white">{c.letter}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-md mb-2 ${a.text}`}>{c.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{c.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <Target className="size-3.5 text-coral" />
            <span className="text-overline text-coral">The Vision Forward</span>
          </div>
          <h2 className="text-display-md mb-4">Where the philosophy is headed.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The vision is not ubiquity. The vision is precision — reaching the decision-makers who can apply the method, and giving them the tools to do so. The body of work continues to expand.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {VISION_PILLARS.slice(0, 6).map((v, i) => {
            const a = ACCENT[v.accent];
            return (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3.5">
                  <div className={`size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg ring-2 ${a.ring} shrink-0`}>
                    <v.icon className="size-5 text-white" />
                  </div>
                  <div>
                    <h3 className={`text-heading-md mb-2 ${a.text}`}>{v.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{v.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <Compass className="size-3.5 text-coral" />
            <span className="text-overline text-coral">The LITHVIK Philosophy</span>
          </div>
          <h2 className="text-display-md mb-4">Seven words, seven principles.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The name is not a brand label. It is a philosophy encoded into seven Latin-derived words — each representing a principle that governs everything. A clear-sighted, infinitely innovative kingdom founded on honor, technological mastery, and victorious leadership.
          </p>
        </div>
        <FeatureGridBlock features={LITHVIK_TENETS} columns={3} />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <CheckCircle2 className="size-3.5 text-denim" />
            <span className="text-overline text-denim">The Ten Values</span>
          </div>
          <h2 className="text-display-md mb-4">Core values that govern every engagement.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Not aspirations. Operational commitments — embedded in the five-step methodology, the six C&apos;s, the LITHVIK philosophy, and the operational infrastructure that governs eighteen countries and nine platforms.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {TEN_VALUES.map((value, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim", "lime", "coral", "red", "purple"] as const;
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className="text-overline text-muted-foreground mb-1">Value {String(i + 1).padStart(2, "0")}</div>
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{value.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{value.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-5`}>
            <Quote className="size-3.5 text-lime" />
            <span className="text-overline text-lime">Why Philosophy Matters</span>
          </div>
          <h2 className="text-display-md mb-4">A clear operating philosophy is the difference that compounds.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            An organization without a clear philosophy is an organization running on inertia. It moves because it has always moved. It produces results because it has always produced results. It survives until the environment changes and the old patterns no longer work. A clear operating philosophy is the difference between an organization that adapts and one that does not. The philosophy provides the foundation for every decision — not as a rigid rulebook, but as a set of principles that can be applied to any situation, at any scale, under any pressure.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The philosophy also provides continuity. When the founder is not in the room, the philosophy governs. When the operator is not on-site, the methodology runs. When the crisis escalates beyond any individual&apos;s capacity, the protocols carry. The philosophy is the mechanism through which an organization transcends any single person — including the person who built it. This is the ultimate test of a philosophy: does it survive contact with reality? The answer is documented in verified outcomes — fifteen campaign victories, over three hundred elite clients, zero security incidents, ninety-nine point nine nine nine nine percent uptime, and teams consistently built to seventy percent women. The philosophy survived. The philosophy produced. The philosophy is the architecture.
          </p>
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-amber-gold/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Brain className="size-10 text-emerald mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Apply the operating system.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              The philosophy is not theoretical. Every belief is grounded in verified operational data. For organizations seeking the operating method applied to their own context, the engagement channels are open.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/about/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Crown className="size-4" />
                About Dr. Jyoti Kush
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "About Dr. Jyoti Kush", description: "The full story. Physician. Operator. Architect of sovereign-scale operations.", href: "/about/", icon: Crown, accent: "red" },
          { title: "The Journey", description: "Four phases: physician → privacy → decade → operator.", href: "/about/journey/", icon: Compass, accent: "purple" },
          { title: "Recognition", description: "Nobel Peace Prize, WEF, and sovereign stages.", href: "/about/recognition/", icon: Award, accent: "amber-gold" },
          { title: "Frequently Asked", description: "Every question answered, every claim verified.", href: "/about/frequently-asked/", icon: Brain, accent: "cyan" },
        ]}
      />
    </>
  );
}
